import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { combineLatest } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables } from 'chart.js';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';
import { DateRangePickerComponent } from './components/date-range-picker/date-range-picker.component';
import { DashboardKpiCardsComponent } from './components/dashboard-kpi-cards/dashboard-kpi-cards.component';
import { DashboardChartsComponent } from './components/dashboard-charts/dashboard-charts.component';
import { AppSelectorComponent } from './components/app-selector/app-selector.component';
import { DashboardDataService } from './services/dashboard-data.service';
import { DashboardChartService } from './services/dashboard-chart.service';
import { AuthService } from '../../services/auth.service';
import { MfaWizardComponent } from '../auth/mfa-wizard.component';

Chart.register(...registerables);

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    BaseChartDirective, 
    DragDropModule, 
    ThinkingOrbComponent,
    DateRangePickerComponent,
    DashboardKpiCardsComponent,
    DashboardChartsComponent,
    AppSelectorComponent,
    MfaWizardComponent
  ],
  template: `
    <div class="p-8 max-w-[1600px] mx-auto">
      <header class="mb-8 flex justify-between items-end border-b border-zaxis-border pb-4">
        <div>
          <h1 class="text-3xl font-bold text-white uppercase tracking-tight">Analíticas Globales</h1>
          <p class="text-zaxis-accent mt-1 text-[11px] font-mono tracking-widest uppercase">Visión general de rendimiento y comportamiento</p>
        </div>
        <div class="flex gap-2 items-center">
          <app-app-selector 
            [apps]="dataService.availableApps()" 
            [selectedApp]="dataService.selectedApp()" 
            (appChange)="onAppChange($event)">
          </app-app-selector>

          <!-- Professional Date Range Dropdown -->
          <app-date-range-picker
            [selectedTimeRange]="dataService.selectedTimeRange()"
            [customStartDate]="dataService.customStartDate()"
            [customEndDate]="dataService.customEndDate()"
            (rangeChange)="onRangeChange($event)">
          </app-date-range-picker>
        </div>
      </header>

      <!-- Custom Brutalist Skeleton Loading State -->
      <div *ngIf="dataService.loading()">
        <!-- Skeleton KPI Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div *ngFor="let mock of [1,2,3,4,5]" class="skeleton-box p-5 h-[120px] flex flex-col justify-between">
            <div class="skeleton-text w-3/4"></div>
            <div class="skeleton-text title w-1/2" style="margin:0"></div>
          </div>
        </div>
        <!-- Skeleton Charts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div *ngFor="let mock of [1,2,3,4]" class="skeleton-box p-5 h-[340px] flex flex-col">
            <div class="flex justify-between mb-4 border-b border-[#333] pb-3">
              <div class="skeleton-text w-1/4" style="margin:0"></div>
              <div class="skeleton-text w-8" style="margin:0"></div>
            </div>
            <div class="flex-grow flex items-end justify-between gap-4">
              <div class="skeleton-text w-full h-[60%]"></div>
              <div class="skeleton-text w-full h-[80%]"></div>
              <div class="skeleton-text w-full h-[40%]"></div>
              <div class="skeleton-text w-full h-[100%]"></div>
              <div class="skeleton-text w-full h-[50%]"></div>
              <div class="skeleton-text w-full h-[70%]"></div>
            </div>
          </div>
        </div>
      </div>
      
      <div *ngIf="!dataService.loading()">
      <!-- KPI Cards -->
      <app-dashboard-kpi-cards
        [sessionsCount]="dataService.sessions().length"
        [crashCount]="dataService.crashCount()"
        [crashRate]="dataService.crashRate()"
        [globalAvgFps]="dataService.globalAvgFps()"
        [deviceCount]="dataService.deviceCount()"
        [globalAvgApdex]="dataService.globalAvgApdex()">
      </app-dashboard-kpi-cards>

      <!-- Charts Area -->
      <app-dashboard-charts
        *ngIf="dataService.sessions().length > 0"
        [widgets]="dataService.widgets()"
        [getChartDataFn]="getChartData.bind(this)"
        [getChartOptionsFn]="getChartOptions.bind(this)"
        [getChartWidthFn]="getChartWidth.bind(this)"
        (widgetDropped)="dropWidget($event)">
      </app-dashboard-charts>

      <!-- Empty State -->
      <div *ngIf="!dataService.loading() && dataService.sessions().length === 0" class="text-center py-20 bg-black rounded-none border border-zaxis-border">
        <div class="flex justify-center mb-4">
          <i class="ph ph-terminal text-6xl text-gray-600"></i>
        </div>
        <p class="text-gray-400 text-sm font-mono uppercase tracking-widest">Sin señal de telemetría</p>
        <p class="text-gray-500 text-xs font-mono uppercase tracking-widest mt-2">Esperando conexión de dispositivo XR...</p>
      </div>

      </div>
      
      <app-mfa-wizard *ngIf="!authLoading && (mfaWizardVisible || !isEmailVerified)" [enforceEmailVerify]="!isEmailVerified" (onComplete)="mfaCompleted()" (onSkip)="mfaSkipped()"></app-mfa-wizard>

    </div>
  `
})
export class DashboardComponent implements OnInit {
  dataService = inject(DashboardDataService);
  chartService = inject(DashboardChartService);
  authService = inject(AuthService);
  private router = inject(Router);

  isMfaEnrolled = true;
  isEmailVerified = true;
  authLoading = true;
  mfaWizardVisible = false;
  platformId = inject(PLATFORM_ID);

  ngOnInit() {
    this.checkMfaStatus();
  }

  checkMfaStatus() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    combineLatest([
      this.authService.emailVerified$,
      this.authService.user$,
      this.authService.authLoading$
    ]).subscribe(([verified, user, loading]) => {
      this.authLoading = loading;
      if (loading) return; // No actuar hasta tener el estado real
      
      this.isEmailVerified = verified === true;
      if (user) {
        this.isMfaEnrolled = this.authService.isMfaEnrolled();
        if (!this.isMfaEnrolled && this.isEmailVerified) {
          this.mfaWizardVisible = true;
        } else {
          this.mfaWizardVisible = false;
        }
      }
    });
  }

  mfaCompleted() {
    this.isMfaEnrolled = true;
    this.mfaWizardVisible = false;
  }

  mfaSkipped() {
    this.mfaWizardVisible = false;
  }

  onRangeChange(event: { range: string, start?: string, end?: string }) {
    this.dataService.updateFilters(
      this.dataService.selectedApp(), 
      event.range, 
      event.start || '', 
      event.end || ''
    );
  }

  onAppChange(app: string) {
    this.dataService.updateFilters(
      app, 
      this.dataService.selectedTimeRange(), 
      this.dataService.customStartDate(), 
      this.dataService.customEndDate()
    );
  }

  dropWidget(event: CdkDragDrop<any[]>) {
    const currentWidgets = [...this.dataService.widgets()];
    moveItemInArray(currentWidgets, event.previousIndex, event.currentIndex);
    this.dataService.widgets.set(currentWidgets);
    this.dataService.saveLayout(currentWidgets.map(w => w.id));
  }

  // Dynamic accessors for the generic app-dashboard-charts component
  getChartData(widget: any) {
    return widget.dataSignal();
  }

  getChartOptions(widget: any) {
    return widget.options;
  }

  getChartWidth(widget: any): number {
    const data = widget.dataSignal();
    const points = data?.labels?.length || 0;
    return Math.max(100, points * 30);
  }

  viewSession(session: any) {
    this.router.navigate(['/session', session.id], { state: { session } });
  }
}
